import React, { useEffect, useState } from "react";
import { Button, Group, Paper, Text } from "@mantine/core";
import { getAppBranding } from "@/utils/appBranding";
import { useAppUpdate } from "@/pwa/updateStore";

const DISMISS_KEY = "pm-pwa-install-dismissed-v1";

function isStandaloneMode() {
  return window.matchMedia("(display-mode: standalone)").matches || (window.navigator as any).standalone === true;
}

export default function PwaInstallPrompt() {
  const appUpdate = useAppUpdate();
  const branding = getAppBranding();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [installed, setInstalled] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      setDismissed(window.localStorage.getItem(DISMISS_KEY) === "1");
    } catch {
      setDismissed(false);
    }
    setInstalled(isStandaloneMode());

    const displayModeQuery = window.matchMedia("(display-mode: standalone)");

    const handleBeforeInstallPrompt = (event: Event) => {
      const installEvent = event as BeforeInstallPromptEvent;
      installEvent.preventDefault();
      setDeferredPrompt(installEvent);
    };

    const handleAppInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
      try {
        window.localStorage.removeItem(DISMISS_KEY);
      } catch {}
    };

    const handleDisplayModeChange = () => {
      if (isStandaloneMode()) {
        setInstalled(true);
        setDeferredPrompt(null);
      }
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);
    displayModeQuery.addEventListener?.("change", handleDisplayModeChange);

    return () => {
      window.removeEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
      window.removeEventListener("appinstalled", handleAppInstalled);
      displayModeQuery.removeEventListener?.("change", handleDisplayModeChange);
    };
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISS_KEY, "1");
    } catch {}
  };

  const handleInstall = async () => {
    if (!deferredPrompt) {
      return;
    }

    setInstalling(true);
    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === "accepted") {
        setInstalled(true);
      }
    } catch (error) {
      console.error("PWA install prompt failed", error);
    } finally {
      setDeferredPrompt(null);
      setInstalling(false);
    }
  };

  if (appUpdate || !deferredPrompt || dismissed || installed) {
    return null;
  }

  return (
    <Paper
      withBorder
      shadow="lg"
      radius="xl"
      p="md"
      style={{
        position: "fixed",
        right: 16,
        bottom: 16,
        zIndex: 1000,
        width: "min(320px, calc(100vw - 32px))",
        background: "#fffef3",
        borderColor: "#E7D58E",
      }}
    >
      <Text fw={700} c="#111111" mb={4}>
        {branding.installPromptTitle}
      </Text>
      <Text size="sm" c="#5c5130" mb="md">
        {branding.installPromptDescription}
      </Text>
      <Group justify="flex-end" gap="sm">
        <Button variant="subtle" color="dark" onClick={handleDismiss}>
          Not now
        </Button>
        <Button color="dark" bg="#111111" onClick={handleInstall} loading={installing}>
          Install app
        </Button>
      </Group>
    </Paper>
  );
}
